import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import classNames from "classnames";
import { ArrowRight, Search, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch, useSelector } from "react-redux";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import {
  getChatConversationPath,
  getMemoryDetailPath,
} from "../../Router/routes";
import { listMemoriesAction } from "../../state/memories/actions";
import { Memory as MemoryType } from "../../state/memories/types";
import {
  formatRecordingDuration,
  getRecommendedQuestionPrompts,
} from "../../state/memories/utils";
import { State } from "../../state/state";
import { Memory } from "./Memory/Memory";
import styles from "./styles.stylesheet.module.scss";

const formatTotalDuration = (durationSeconds: number) => {
  const mins = Math.max(1, Math.round(durationSeconds / 60));
  if (mins < 60) return `${mins} min`;

  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return remainingMins ? `${hours} hr ${remainingMins} min` : `${hours} hr`;
};

const formatDateLabel = (date: Date) => {
  return date.toLocaleDateString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

const formatGreeting = (date: Date) => {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const formatMemoryTime = (dateValue: string) => {
  return new Date(dateValue).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatMemoryDate = (dateValue: string) => {
  return new Date(dateValue).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatMonthYear = (dateValue: string) => {
  return new Date(dateValue).toLocaleDateString([], {
    month: "long",
    year: "numeric",
  });
};

const dateInputValue = (date: Date | null) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDayDifference = (dateValue: string) => {
  const date = new Date(dateValue);
  const today = new Date();
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  return Math.round(
    (startOfToday.getTime() - startOfDate.getTime()) / (24 * 60 * 60 * 1000),
  );
};

const getGroupLabel = (dateValue: string) => {
  const dayDifference = getDayDifference(dateValue);
  if (dayDifference === 0) return "Today";
  if (dayDifference === 1) return "Yesterday";
  if (dayDifference < 7) return "This week";
  return formatMonthYear(dateValue);
};

const getLatestMemoryDateLabel = (dateValue: string) => {
  const dayDifference = getDayDifference(dateValue);
  if (dayDifference === 0) return "Today";
  if (dayDifference === 1) return "Yesterday";
  return formatMemoryDate(dateValue);
};

const groupMemories = (items: MemoryType[]) => {
  const groups: { label: string; memories: MemoryType[] }[] = [];

  for (const item of items) {
    const label = getGroupLabel(item.started_at);
    const existingGroup = groups.find((group) => group.label === label);
    if (existingGroup) {
      existingGroup.memories.push(item);
    } else {
      groups.push({ label, memories: [item] });
    }
  }

  return groups;
};

const isRenderableMemory = (memory: MemoryType) => {
  return Boolean(
    memory.id &&
    memory.started_at &&
    Number.isFinite(new Date(memory.started_at).getTime()),
  );
};

const truncatePreview = (text: string, maxLength = 150) => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}...`;
};

const latestMemoryDescription = (memory: MemoryType) => {
  if (memory.is_processing) {
    return "Replay is processing this audio. Its transcript, summary, and questions will be ready shortly.";
  }

  const preview =
    memory.summary?.quick_summary ||
    "Your transcript and summary are ready to explore.";

  return truncatePreview(preview);
};

const LatestMemorySkeleton = () => (
  <section
    className={classNames(styles.latestMemoryCard, styles.latestMemorySkeleton)}
    aria-label="Loading latest memory"
    aria-busy="true"
  >
    <div className={styles.latestMemoryContent}>
      <Skeleton className={styles.darkSkeleton} height={12} width={126} />
      <Skeleton className={styles.darkSkeleton} height={66} width="74%" />
      <Skeleton className={styles.darkSkeleton} height={18} width="88%" />
      <Skeleton className={styles.darkSkeleton} height={14} width={220} />
    </div>
  </section>
);

const MemoriesGridSkeleton = () => (
  <section className={styles.memoriesGroup} aria-label="Loading memories">
    <div className={styles.memoriesGroupHeader}>
      <Skeleton height={30} width={120} />
      <Skeleton height={14} width={90} />
    </div>
    <div className={styles.memoriesGrid}>
      {Array.from({ length: 6 }, (_, index) => (
        <article className={styles.memoryCardSkeleton} key={index}>
          <div className={styles.memoryCardSkeletonTopline}>
            <Skeleton height={14} width={72} />
            <Skeleton height={24} radius={999} width={54} />
          </div>
          <Skeleton height={28} width="68%" />
          <Skeleton height={16} width="100%" />
          <Skeleton height={16} width="82%" />
          <Skeleton height={32} width={126} />
        </article>
      ))}
    </div>
  </section>
);

const GlobalSearchSkeleton = () => (
  <section
    className={styles.globalSearchPanel}
    aria-label="Loading global chat"
    aria-busy="true"
  >
    <div className={styles.globalSearchInputSkeleton}>
      <Skeleton height={20} radius="50%" width={20} />
      <Skeleton height={18} width="62%" />
      <Skeleton height={36} radius="50%" width={36} />
    </div>
    <div className={styles.tryRowSkeleton}>
      <Skeleton height={14} width={24} />
      <Skeleton height={31} radius={999} width={154} />
      <Skeleton height={31} radius={999} width={126} />
      <Skeleton height={31} radius={999} width={146} />
    </div>
  </section>
);

export const Memories = () => {
  const dispatch = useDispatch<ThunkDispatch<State, unknown, AnyAction>>();
  const navigate = useNavigate();
  const user = useSelector((state: State) => state.user.user);
  const memories = useSelector((state: State) => state.memories.items);
  const hasLoadedMemories = useSelector(
    (state: State) => state.memories.hasLoaded,
  );

  const [isLoading, setIsLoading] = useState(!hasLoadedMemories);
  const [globalSearch, setGlobalSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);

  const initMemories = useCallback(async () => {
    if (!user) return;

    if (hasLoadedMemories) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    await dispatch(listMemoriesAction(user.id));
    setIsLoading(false);
  }, [user, hasLoadedMemories, dispatch]);

  useEffect(() => {
    initMemories();
  }, [initMemories]);

  const sortedMemories = useMemo(() => {
    return Object.values(memories)
      .filter(isRenderableMemory)
      .sort(
        (a, b) =>
          new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
      );
  }, [memories]);

  const filteredMemories = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query && !dateFrom && !dateTo) return sortedMemories;

    return sortedMemories.filter((memory) => {
      if (query && !memory.name.toLowerCase().includes(query)) return false;

      const started = new Date(memory.started_at);
      if (dateFrom) {
        const fromStart = new Date(dateFrom);
        fromStart.setHours(0, 0, 0, 0);
        if (started < fromStart) return false;
      }
      if (dateTo) {
        const toEnd = new Date(dateTo);
        toEnd.setHours(23, 59, 59, 999);
        if (started > toEnd) return false;
      }
      return true;
    });
  }, [sortedMemories, searchQuery, dateFrom, dateTo]);

  const memoryGroups = useMemo(
    () => groupMemories(filteredMemories),
    [filteredMemories],
  );
  const totalMemories = sortedMemories.length;
  const totalDurationSeconds = sortedMemories.reduce(
    (total, memory) => total + memory.duration_seconds,
    0,
  );
  const latestMemory = sortedMemories[0];
  const currentDate = new Date();
  const firstName = user?.first_name?.trim() || "there";
  const hasFilters = Boolean(searchQuery || dateFrom || dateTo);
  const waveformBars = [
    22, 36, 54, 31, 68, 42, 76, 52, 88, 61, 96, 72, 48, 82, 57, 70, 39, 55, 29,
    41,
  ];
  const tryPrompts = useMemo(
    () =>
      getRecommendedQuestionPrompts(sortedMemories, [
        "What happened last week?",
        "What did I discuss recently?",
        "What should I follow up on?",
      ]),
    [sortedMemories],
  );

  const startGlobalChat = useCallback(
    (prompt: string) => {
      const trimmed = prompt.trim();
      const path = trimmed
        ? `${getChatConversationPath("new")}?prompt=${encodeURIComponent(
            trimmed,
          )}`
        : getChatConversationPath("new");
      navigate(path);
    },
    [navigate],
  );

  const handleGlobalSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    startGlobalChat(globalSearch);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setDateFrom(null);
    setDateTo(null);
  };

  return (
    <main className={styles.memoriesPage}>
      <section className={styles.memoriesIntro}>
        <p className={styles.currentDate}>{formatDateLabel(currentDate)}</p>
        <h1>
          {formatGreeting(currentDate)}, {firstName}.
        </h1>
        {!isLoading && totalMemories > 0 && (
          <p className={styles.captureSummary}>
            You&apos;ve captured <strong>{totalMemories}</strong>{" "}
            {totalMemories === 1 ? "memory" : "memories"} across{" "}
            <strong>{formatTotalDuration(totalDurationSeconds)}</strong> of
            audio.
          </p>
        )}
      </section>

      {isLoading && !latestMemory ? (
        <LatestMemorySkeleton />
      ) : (
        latestMemory && (
          <Link
            className={classNames(styles.latestMemoryCard, {
              [styles.latestMemoryCardDisabled]: latestMemory.is_processing,
            })}
            to={getMemoryDetailPath(latestMemory.id)}
            aria-disabled={latestMemory.is_processing}
            onClick={(event) => {
              if (latestMemory.is_processing) event.preventDefault();
            }}
          >
            <div className={styles.latestMemoryContent}>
              <span className={styles.latestEyebrow}>Latest memory</span>
              <h2>
                {latestMemory.is_processing ? "Processing" : latestMemory.name}
              </h2>
              <p>{latestMemoryDescription(latestMemory)}</p>
              <div className={styles.latestMeta}>
                <span>{getLatestMemoryDateLabel(latestMemory.started_at)}</span>
                <span>{formatMemoryTime(latestMemory.started_at)}</span>
                <span>
                  {formatRecordingDuration(latestMemory.duration_seconds)}
                </span>
              </div>
            </div>
            <span className={styles.latestWaveform} aria-hidden="true">
              {waveformBars.map((height, index) => (
                <span
                  key={`${height}-${index}`}
                  style={
                    {
                      "--waveform-height": `${height}%`,
                    } as React.CSSProperties
                  }
                />
              ))}
            </span>
            <span className={styles.latestArrow} aria-hidden="true">
              <ArrowRight size={34} strokeWidth={1.8} />
            </span>
          </Link>
        )
      )}

      {isLoading ? (
        <GlobalSearchSkeleton />
      ) : (
        <form
          className={styles.globalSearchPanel}
          aria-label="Ask across memories"
          onSubmit={handleGlobalSearchSubmit}
        >
          <div className={styles.globalSearchInput}>
            <Search aria-hidden="true" size={20} strokeWidth={1.9} />
            <input
              type="search"
              value={globalSearch}
              onChange={(event) => setGlobalSearch(event.target.value)}
              placeholder="Ask across memories..."
              aria-label="Ask across memories"
            />
            <button type="submit" aria-label="Start chat">
              <ArrowRight aria-hidden="true" size={19} strokeWidth={2} />
            </button>
          </div>
          <div className={styles.tryRow}>
            <span>Try:</span>
            {tryPrompts.map((prompt) => (
              <button
                type="button"
                key={prompt}
                onClick={() => startGlobalChat(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
        </form>
      )}

      {!isLoading && totalMemories > 0 && (
        <section className={styles.memoriesCollectionHeader}>
          <div>
            <p>Library</p>
            <h2>All memories</h2>
          </div>
          <span>
            {filteredMemories.length}{" "}
            {filteredMemories.length === 1 ? "memory" : "memories"}
          </span>
        </section>
      )}

      {!isLoading && totalMemories > 0 && (
        <section className={styles.memoriesFilters} aria-label="Memory filters">
          <label className={styles.filterSearch}>
            <span>Search</span>
            <span className={styles.filterInputShell}>
              <Search aria-hidden="true" size={17} strokeWidth={1.9} />
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search by title"
              />
            </span>
          </label>
          <label>
            <span>From</span>
            <input
              type="date"
              value={dateInputValue(dateFrom)}
              onChange={(event) =>
                setDateFrom(
                  event.target.value
                    ? new Date(`${event.target.value}T00:00:00`)
                    : null,
                )
              }
            />
          </label>
          <label>
            <span>To</span>
            <input
              type="date"
              value={dateInputValue(dateTo)}
              onChange={(event) =>
                setDateTo(
                  event.target.value
                    ? new Date(`${event.target.value}T00:00:00`)
                    : null,
                )
              }
            />
          </label>
          {hasFilters && (
            <button
              className={styles.clearFilters}
              type="button"
              onClick={clearFilters}
            >
              <X aria-hidden="true" size={15} />
              Clear
            </button>
          )}
        </section>
      )}

      {isLoading ? (
        <MemoriesGridSkeleton />
      ) : totalMemories === 0 ? (
        <div className={styles.appCenterState}>
          <h2>No memories yet</h2>
          <p>Memories transferred from the mobile app will appear here.</p>
        </div>
      ) : filteredMemories.length === 0 ? (
        <div className={styles.appCenterState}>
          <h2>No memories match your filters</h2>
          <p>Try another title or date range.</p>
          <button type="button" onClick={clearFilters}>
            Clear filters
          </button>
        </div>
      ) : (
        <section aria-label="Memories">
          {memoryGroups.map((group) => (
            <div className={styles.memoriesGroup} key={group.label}>
              <div className={styles.memoriesGroupHeader}>
                <h2>{group.label}</h2>
                <span>
                  {group.memories.length}{" "}
                  {group.memories.length === 1 ? "memory" : "memories"}
                </span>
              </div>
              <div className={styles.memoriesGrid}>
                {group.memories.map((item) => (
                  <Memory key={item.id} item={item} />
                ))}
              </div>
            </div>
          ))}
        </section>
      )}
    </main>
  );
};
